import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { BriefField } from "@/lib/api";

export type FieldValue = string | string[] | boolean;

/** Default value for each field type (toggle→false, multiselect→[], else ""). */
export function initFieldValues(fields: BriefField[]): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const f of fields) {
    out[f.key] = f.type === "toggle" ? false : f.type === "multiselect" ? [] : "";
  }
  return out;
}

/** Field values seeded from an existing brief_data object (defaults for keys absent
 *  in the data), so a form can show the project's current values. */
export function seedFieldValues(
  fields: BriefField[],
  briefData: Record<string, unknown> | null | undefined,
): Record<string, FieldValue> {
  const out = initFieldValues(fields);
  if (!briefData) return out;
  for (const f of fields) {
    const v = briefData[f.key];
    if (v === undefined || v === null) continue;
    if (f.type === "toggle") out[f.key] = Boolean(v);
    else if (f.type === "multiselect") out[f.key] = Array.isArray(v) ? (v as string[]) : [];
    else out[f.key] = String(v);
  }
  return out;
}

export function isFieldEmpty(field: BriefField, value: FieldValue | undefined): boolean {
  if (field.type === "toggle") return false; // a toggle is always answered
  if (field.type === "multiselect") return !Array.isArray(value) || value.length === 0;
  return typeof value !== "string" || value.trim() === "";
}

/** Assemble the non-empty dynamic field values into a brief_data fragment. */
export function buildBriefData(
  fields: BriefField[],
  values: Record<string, FieldValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const value = values[f.key];
    if (f.type === "toggle") {
      out[f.key] = Boolean(value);
    } else if (f.type === "multiselect") {
      if (Array.isArray(value) && value.length > 0) out[f.key] = value;
    } else if (typeof value === "string" && value.trim()) {
      out[f.key] = value.trim();
    }
  }
  return out;
}

interface Props {
  fields: BriefField[];
  values: Record<string, FieldValue>;
  onChange: (key: string, value: FieldValue) => void;
}

/** Renders role-specific brief fields from a scenario's brief_schema. */
export function DynamicBriefFields({ fields, values, onChange }: Props) {
  const toggleMulti = (key: string, option: string) => {
    const current = Array.isArray(values[key]) ? (values[key] as string[]) : [];
    onChange(
      key,
      current.includes(option) ? current.filter((o) => o !== option) : [...current, option],
    );
  };

  const renderField = (field: BriefField) => {
    const value = values[field.key];
    const labelEl = (
      <label className="text-sm font-medium">
        {field.label}
        {field.required ? <span className="text-destructive"> *</span> : null}
      </label>
    );

    switch (field.type) {
      case "textarea":
        return (
          <div key={field.key} className="space-y-1.5">
            {labelEl}
            <Textarea
              value={typeof value === "string" ? value : ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              rows={3}
            />
          </div>
        );

      case "select":
        return (
          <div key={field.key} className="space-y-1.5">
            {labelEl}
            <Select
              value={typeof value === "string" && value ? value : undefined}
              onValueChange={(v) => onChange(field.key, v)}
            >
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder={field.placeholder || "Select…"} />
              </SelectTrigger>
              <SelectContent>
                {(field.options ?? []).map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "multiselect": {
        const selected = Array.isArray(value) ? value : [];
        return (
          <div key={field.key} className="space-y-1.5">
            {labelEl}
            <div className="flex flex-wrap gap-2">
              {(field.options ?? []).map((opt) => {
                const on = selected.includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => toggleMulti(field.key, opt)}
                    aria-pressed={on}
                    className={
                      "px-3 py-1.5 rounded-full border text-sm transition-colors " +
                      (on
                        ? "border-foreground bg-foreground text-background"
                        : "border-border hover:bg-muted")
                    }
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        );
      }

      case "toggle":
        return (
          <label key={field.key} className="flex items-center gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(field.key, e.target.checked)}
              className="w-4 h-4 rounded border-muted-foreground/30"
            />
            <span className="text-sm font-medium">
              {field.label}
              {field.required ? <span className="text-destructive"> *</span> : null}
            </span>
          </label>
        );

      case "text":
      default:
        return (
          <div key={field.key} className="space-y-1.5">
            {labelEl}
            <Input
              value={typeof value === "string" ? value : ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              placeholder={field.placeholder}
            />
          </div>
        );
    }
  };

  return <>{fields.map(renderField)}</>;
}

export default DynamicBriefFields;
